import styled, { css } from 'styled-components';

import { ripple, rotate } from 'utils/style/animations';
import {
  black,
  blue,
  gray,
  green,
  red,
  darkGray,
  shadeColor,
  orange,
  whitesmoke,
  exactWhite,
  btnBoxShadow,
  formColor1,
  white,
} from 'utils/style/colors';

const getBtnColor = (color: string) => {
  switch (color) {
    case 'green-outline':
      return green;
    case 'red-outline':
      return red;
    case 'blue-outline':
      return blue;
    case 'orange-outline':
      return orange;
    case 'gray':
      return gray;
    case 'red':
    case 'green':
    case 'orange':
    case 'black':
    case 'blue':
    case 'dark-gray':
      return 'var(--exact-white)';
    case 'primary-outline':
      return 'var(--primary-form-color)';
    case 'primary-color':
      return white;
    default:
      return black;
  }
};

const getBtnHoverColor = (color: string) => {
  switch (color) {
    case 'primary':
      return green;
    case 'gray':
      return gray;
    case 'white':
      return black;
    case 'primary-color':
    case 'primary-outline':
      return 'var(--form-color-1)';
    default:
      return exactWhite;
  }
};

const rippleAnimation = css`
  ${ripple} 1s ease-out;
`;

const rotateAnimation = css`
  ${rotate} 0.5s infinite ease-out;
`;

interface IButtonEleProps {
  size?: string;
  color?: string;
}

interface IButtonChildProps {
  loading?: boolean;
}

interface ISpinnerProps {
  color?: string;
}

export const ButtonWrapper = styled('div')`
  position: absolute;
`;

export const ButtonEle = styled('button')<IButtonEleProps>`
  flex: none;
  display: inline-flex;
  overflow: hidden;
  position: relative;
  box-shadow: ${btnBoxShadow};
  text-align: center;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  align-items: center;
  border-style: solid;
  border-width: 1px;
  border-radius: 15px;
  letter-spacing: 0.5px;
  justify-content: center;

  color: ${(props) => getBtnColor(props.color)} !important;

  & i {
    color: ${(props) => getBtnColor(props.color)} !important;
  }

  padding: ${(props) => {
    switch (props.size) {
      case 'large':
        return '0.7rem 1.5rem';
      case 'small':
        return '0.4rem 0.8rem';
      default:
        return '0.5rem 1.2rem';
    }
  }};
  font-size: ${(props) => {
    switch (props.size) {
      case 'large':
        return '1.3rem';
      case 'small':
        return '0.8rem';
      default:
        return '1rem';
    }
  }};
  border-color: ${(props) => {
    switch (props.color) {
      case 'primary':
        return 'silver';
      case 'green':
      case 'green-outline':
        return green;
      case 'orange':
      case 'orange-outline':
        return orange;
      case 'red':
      case 'red-outline':
        return red;
      case 'blue':
      case 'blue-outline':
        return blue;
      case 'gray':
        return `${whitesmoke}`;
      case 'dark-gray':
        return darkGray;
      case 'primary-outline':
      case 'primary-color':
        return formColor1;
      default:
        return black;
    }
  }};
  background-color: ${(props) => {
    switch (props.color) {
      case 'black':
        return black;
      case 'green':
        return green;
      case 'orange':
        return orange;
      case 'red':
        return red;
      case 'blue':
        return blue;
      case 'gray':
        return `${whitesmoke}`;
      case 'dark-gray':
        return darkGray;
      case 'primary-color':
        return 'var(--form-color-1)';
      default:
        return 'var(--exact-white)';
    }
  }};

  &:after {
    content: '';
    top: 50%;
    left: 50%;
    width: 5px;
    height: 5px;
    opacity: 0;
    position: absolute;
    transform: scale(1, 1) translate(-50%);
    background: rgba(255, 255, 255, 0.2);
    border-radius: 100%;
    transform-origin: 50% 50%;
  }
  &:focus:not(:active)::after {
    animation: ${rippleAnimation};
  }
  &:hover {
    color: ${(props) => getBtnHoverColor(props.color)} !important;

    & i {
      color: ${(props) => getBtnHoverColor(props.color)} !important;
    }

    background-color: ${(props) => {
      switch (props.color) {
        case 'black':
          return shadeColor(black, -10);
        case 'green':
          return shadeColor(green, -10);
        case 'orange':
          return shadeColor(orange, -10);
        case 'red':
          return shadeColor(red, -10);
        case 'blue':
          return shadeColor(blue, -10);
        case 'gray':
          return shadeColor('#f5f5f5', -10);
        case 'red-outline':
          return red;
        case 'green-outline':
          return green;
        case 'orange-outline':
          return orange;
        case 'blue-outline':
          return blue;
        case 'black-outline':
          return black;
        case 'dark-gray':
          return darkGray;
        default:
          return `${whitesmoke}`;
      }
    }};
    border-color: ${(props) => {
      switch (props.color) {
        case 'black':
          return shadeColor(black, -10);
        case 'green':
          return shadeColor(green, -10);
        case 'red':
          return shadeColor(red, -10);
        case 'blue':
          return shadeColor(blue, -10);
        case 'gray':
          return shadeColor('#f5f5f5', -10);
        case 'red-outline':
          return red;
        case 'green-outline':
          return green;
        case 'orange-outline':
          return orange;
        case 'blue-outline':
          return blue;
        case 'black-outline':
          return black;
        case 'dark-gray':
          return darkGray;
        case 'white':
          return black;
        default:
          return `${whitesmoke}`;
      }
    }};
  }

  &:focus {
    color: ${(props) => {
      switch (props.color) {
        case 'green-outline':
          return shadeColor(green, 10);
        case 'orange-outline':
          return shadeColor(orange, 10);
        case 'red-outline':
          return shadeColor(red, 10);
        case 'blue-outline':
          return shadeColor(blue, 10);
        case 'red':
        case 'green':
        case 'orange':
        case 'black':
        case 'blue':
          return 'var(--exact-white)';
        default:
          return shadeColor(black, 10);
      }
    }};
    background-color: ${(props) => {
      switch (props.color) {
        case 'black':
          return shadeColor(black, 10);
        case 'green':
          return shadeColor(green, 10);
        case 'orange':
          return shadeColor(orange, 10);
        case 'red':
          return shadeColor(red, 10);
        case 'blue':
          return shadeColor(blue, 10);
        case 'gray':
          return shadeColor('#f5f5f5', -10);
        default:
          return 'var(--exact-white)';
      }
    }};
  }
  &[disabled] {
    color: silver;
    cursor: not-allowed;
    background: ${whitesmoke};
    border-color: silver;
  }
`;

export const A = ButtonEle.withComponent('a');

export const ButtonChild = styled('span')<IButtonChildProps>`
  display: flex;
  opacity: ${(props) => (props.loading ? 0 : 1)};
  transform: ${(props) => (props.loading ? 'scale(2.2)' : 'scale(1)')};
  transition: all 0.2s ease;
  align-items: center;
  font-weight: 400;
  justify-content: space-between;

  & > i {
    padding-inline-end: 5px;
  }
`;

export const Spinner = styled('svg')<ISpinnerProps>`
  width: 18px;
  display: flex;
  position: relative;
  animation: ${rotateAnimation};
  & .path {
    fill: ${(props) => {
      switch (props.color) {
        case 'primary':
        case 'green-outline':
          return green;
        case 'red-outline':
          return red;
        case 'blue-outline':
          return blue;
        case 'black-outline':
          return black;
        case 'orange-outline':
          return black;
        default:
          return props.color;
      }
    }};
  }
`;
